/**
 * Thin web adapter over the SHARED availability core in @tt/games-registry.
 *
 * History (8P program, Phase 0): this file used to be a hand-copied
 * duplicate of the registry's matching+reason logic with hardcoded `?? 4`
 * fallbacks — which would have silently lied ("works with up to 4") the
 * moment the player cap moved. Now the only web-side responsibilities are
 * adapting GameTile (server-shipped formats) and display-label helpers.
 */
import type { GameTile } from '../../lib/api';
import { availabilityForFormats, type CountFormat } from '@tt/games-registry';
import { MAX_PLAYERS } from '@tt/players';

export interface GameAvailability {
  playable: boolean;
  reason: string | null;
}

export function livePlayerCount(lobby: { players: Array<{ isSpectator?: boolean }> } | null): number {
  return lobby?.players.filter((p) => !p.isSpectator).length ?? 0;
}

/** Formats for a tile: the server ships them on every tile; the fallback
 *  (manifest-less tile from an old cache) derives bounds from the tile's
 *  own min/max and the PLATFORM cap — never a hardcoded count. */
function formatsForTile(g: GameTile): CountFormat[] {
  if (g.playerFormats?.length) return g.playerFormats;
  const min = Math.max(2, g.minPlayers ?? 2);
  const max = Math.max(min, g.maxPlayers ?? MAX_PLAYERS);
  return [{ label: `${min}-${max} players`, minPlayers: min, maxPlayers: max, countMode: 'range' }];
}

export function gameAvailability(g: GameTile, playerCount: number): GameAvailability {
  const { playable, reason } = availabilityForFormats(formatsForTile(g), playerCount);
  return { playable, reason };
}

export function playerReqLabel(g: GameTile): string {
  const formats = g.playerFormats ?? [];
  if (formats.length === 1) {
    const f = formats[0]!;
    if (f.countMode === 'exact' && f.minPlayers === f.maxPlayers) return `${f.minPlayers}p`;
    return `${f.minPlayers}-${f.maxPlayers}p`;
  }
  const min = Math.max(2, g.minPlayers ?? 2);
  const max = Math.max(min, g.maxPlayers ?? MAX_PLAYERS);
  return min === max ? `${min}p` : `${min}-${max}p`;
}
